import {Component, EffectRef, EventEmitter, Input, OnDestroy, OnInit, Output, effect, inject} from '@angular/core';
import {FormBuilder, FormGroup} from "@angular/forms";
import {DialogService} from "../../../shared/services/dialog.service";
import {LanguageService} from "../../../shared/services/language.service";
import {DialogModule} from "@syncfusion/ej2-angular-popups";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {Subscription, take} from "rxjs";
import {ApiService} from "../../../shared/services/api.service";
import {ToasterService} from "../../../shared/services/toaster.service";
import {ProfileService} from '../../../shared/services/profile.service';
import {ProfileResource} from '../../../shared/types/user/profileResource';
import {UserProfileUpdateRequest} from '../../../shared/types/user/userProfileUpdateRequest';
import {ProfilePushSettingsComponent} from "../profile-push-settings/profile-push-settings.component";

@Component({
    selector: 'app-dialog-profile-push',
    imports: [
    DialogModule,
    HeadlineComponent,
    TooltipComponent,
    TranslatePipe,
    FormButtonComponent,
    ProfilePushSettingsComponent
],
    templateUrl: './dialog-profile-push.component.html',
    styleUrl: './dialog-profile-push.component.scss'
})
export class DialogProfilePushComponent implements OnInit, OnDestroy {
  public dialogService: DialogService = inject(DialogService);
  private profileService: ProfileService = inject(ProfileService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private apiService: ApiService = inject(ApiService);
  private toasterService: ToasterService = inject(ToasterService);
  private languageService: LanguageService = inject(LanguageService);

  @Input() public visible: boolean = false;
  @Output() public  visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public profilePushForm: FormGroup = this.formBuilder.group({
    push_disable_all: [''],
    push_disable_mobile: [''],
    push_disable_email: [''],
    push_disable_browser: [''],
    push_disable_chat: [''],
  });
  private subscriptions: Subscription[] = [];

  public ngOnInit(): void {
    Object.keys(this.profilePushForm.controls)
      .filter(k => k.startsWith('push'))
      .forEach(key => {
        this.profilePushForm.get(key)?.valueChanges.subscribe(val => {
          this.checkPushFields(key, val);
        });
      });
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

    private profileEffect: EffectRef = effect(() => {
    const profile = this.profileService.profile();
    if (profile) {
      this.patchForm(profile);
    }
  });

    private patchForm(profile: ProfileResource): void {
      this.profilePushForm.patchValue({
        push_disable_all: !profile.notifications.send_to_mobile && !profile.notifications.send_to_web && !profile.notifications.send_from_chat,
        push_disable_mobile: !profile.notifications.send_to_mobile,
        push_disable_email: !profile.notifications.send_to_mail,
        push_disable_browser: !profile.notifications.send_to_web,
        push_disable_chat: !profile.notifications.send_from_chat,
      }, {emitEvent: false});
    }

  public close(): void {
    this.visibleChange.emit(false);
  }

  public save(): void {
    const profileUser = this.profileService.profile()?.user;
    if (!profileUser) {
      return;
    }

    const requestData: UserProfileUpdateRequest = {
        user: {
          title: profileUser.title,
          first_name: profileUser.first_name,
          last_name: profileUser.last_name,
          email: profileUser.email ?? '',
          is_email_private: profileUser.is_email_private,
          telephone: profileUser.telephone,
          is_telephone_private: profileUser.is_telephone_private,
          mobile: profileUser.mobile,
          is_mobile_private: profileUser.is_mobile_private,
        },
        notifications: {
          send_to_web: !this.profilePushForm.controls['push_disable_browser'].value,
          send_to_mobile: !this.profilePushForm.controls['push_disable_mobile'].value,
          send_to_mail: !this.profilePushForm.controls['push_disable_email'].value,
          send_from_chat: !this.profilePushForm.controls['push_disable_chat'].value,
        }
      };
  
      this.apiService.updateUserProfile(requestData)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toasterService.success(
            this.languageService.getTranslationByKey('PROFILENOTIFICATIONSUPDATESUCCESSTITLE'),
            this.languageService.getTranslationByKey('PROFILENOTIFICATIONSUPDATESUCCESSMESSAGE')
          );
          this.profileService.loadUserProfile();
          this.close();
        },
        error: (error) => {
          console.error('Error saving profile notifications:', error);
          this.profileService.loadUserProfile();
        }
      });
  }

  private checkPushFields(key: string, value: boolean) {
    const managedByAllKeys = ['push_disable_mobile', 'push_disable_browser', 'push_disable_chat'];
    const allChecked = managedByAllKeys.every(controlKey => this.profilePushForm.controls[controlKey].value === true);
    const currentEmailValue = this.profilePushForm.controls['push_disable_email'].value;

    if (key === 'push_disable_all' && value) {
      managedByAllKeys.forEach(controlKey => {
        if (!this.profilePushForm.controls[controlKey].value) {
          this.profilePushForm.get(controlKey)?.setValue(true, {emitEvent: false});
        }
      });
      this.profilePushForm.get('push_disable_email')?.setValue(currentEmailValue, {emitEvent: false});
      return;
    }

    if (key === 'push_disable_all' && !value) {
      managedByAllKeys.forEach(controlKey => {
        if (this.profilePushForm.controls[controlKey].value) {
          this.profilePushForm.get(controlKey)?.setValue(false, {emitEvent: false});
        }
      });
      this.profilePushForm.get('push_disable_email')?.setValue(currentEmailValue, {emitEvent: false});
      return;
    }

    if (allChecked && !this.profilePushForm.controls['push_disable_all'].value) {
      this.profilePushForm.patchValue({push_disable_all: true}, {emitEvent: false});
      return;
    }

    if (!allChecked && this.profilePushForm.controls['push_disable_all'].value) {
      this.profilePushForm.patchValue({push_disable_all: false}, {emitEvent: false});
    }
  }
}
